import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Eye, Heart, ShoppingBag, UserRound, Users } from 'lucide-react';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { BrandPeriod, Breakdown, useBrandApi } from '../../../common/hooks/use-brand-api.hook';
import { KpiCard, SectionCard, formatNumber } from '../../../components/design-system/admin';
import { BarList, Funnel, Thumb, TrendChart } from '../../../components/design-system/analytics';
import { useLoader } from '../../../modules/vendor/analytics/viz/use-loader';
import { BrandPage, Change, LoadFailed, PeriodPills, PrivacyFootnote, ShopNameText, SuppressedNotice, Withheld, useBrandMe } from '../../../modules/brand/components/BrandUi';
import { rowLabel, trendPoints, vocab } from '../../../modules/brand/brand-format';

const kpiDelta = (m?: { value: number | null; previous: number | null } | null) =>
    m && m.value != null && m.previous != null ? { current: m.value, previous: m.previous } : undefined;

/** One product across the network: when, where, in which rooms, at which retailers, and how far shoppers go with it. */
export function BrandProductPage() {
    const { uuid = '' } = useParams();
    const { t, i18n } = useTranslation();
    const lang = (i18n.language || 'en').slice(0, 2);
    const api = useBrandApi();
    const { me } = useBrandMe();
    const [period, setPeriod] = useState<BrandPeriod>('30d');
    const { data, loading, error, reload } = useLoader(async () => (me?.connected ? api.product(uuid, period) : null), [uuid, period, me?.connected]);
    const p = data?.product;
    const k = data?.kpis;
    const vsPrevious = t('BRAND_PORTAL.COMMON.VS_PREVIOUS', { count: Number(period.replace('d', '')) });

    const breakdownList = (b: Breakdown | undefined, dim: 'country' | 'city' | 'roomType') => (
        <BarList
            emptyText={loading ? undefined : t('BRAND_PORTAL.PRODUCT.BREAKDOWN_EMPTY')}
            items={[...(b?.rows ?? []), ...(b?.other ? [b.other] : [])].map((r) => ({ key: r.key, label: rowLabel(t, dim, r, lang), value: r.visualizations, secondary: <Change value={r.change.visualizations} /> }))}
        />
    );

    const facts = p
        ? [p.collectionName, p.ref, p.format, p.appearance && t('BRAND_PORTAL.PRODUCT.LOOK', { look: vocab(t, p.appearance).toLowerCase() }), p.colourName, p.finish && vocab(t, p.finish)].filter(Boolean)
        : [];

    return (
        <BrandPage
            testId="brand-product"
            crumb={p?.name ?? t('BRAND_PORTAL.NAV.PRODUCT')}
            title={
                <span className="flex items-center gap-3">
                    <Link to="/brand/demand?groupBy=product" className="text-muted-foreground hover:text-foreground" aria-label={t('BRAND_PORTAL.COMMON.BACK')}>
                        <ArrowLeft className="size-5 rtl:rotate-180" />
                    </Link>
                    <Thumb src={p?.thumbnailUrl} size="lg" />
                    <span className="min-w-0">
                        <span className="block truncate">{p?.name ?? '…'}</span>
                        <span className="block truncate text-sm font-normal text-muted-foreground">{facts.join(' · ')}</span>
                    </span>
                </span>
            }
            actions={<PeriodPills value={period} onChange={setPeriod} />}
        >
            {error && <LoadFailed onRetry={reload} />}

            {data?.suppressed ? (
                <SuppressedNotice reason={data.suppressed.reason} />
            ) : (
                !error && (
                    <>
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
                            <KpiCard testId="brand-product-kpi-viz" label={t('BRAND_PORTAL.KPI.VISUALIZATIONS')} value={formatNumber(k?.visualizations.value)} delta={kpiDelta(k?.visualizations)} hint={vsPrevious} icon={<Eye />} loading={loading} />
                            <KpiCard label={t('BRAND_PORTAL.KPI.SHOPPERS')} value={formatNumber(k?.shoppers.value)} delta={kpiDelta(k?.shoppers)} hint={vsPrevious} icon={<Users />} loading={loading} />
                            <KpiCard label={t('BRAND_PORTAL.KPI.FAVOURITES')} value={formatNumber(k?.favourites.value)} delta={kpiDelta(k?.favourites)} hint={vsPrevious} icon={<Heart />} loading={loading} />
                            <KpiCard label={t('BRAND_PORTAL.KPI.BUY_CLICKS')} value={formatNumber(k?.buyClicks.value)} delta={kpiDelta(k?.buyClicks)} hint={vsPrevious} icon={<ShoppingBag />} loading={loading} />
                            <KpiCard label={t('BRAND_PORTAL.KPI.LEADS')} value={formatNumber(k?.leads.value)} delta={kpiDelta(k?.leads)} hint={vsPrevious} icon={<UserRound />} loading={loading} />
                        </div>

                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-5">
                            <SectionCard className="min-w-0 lg:col-span-3" title={t('BRAND_PORTAL.OVERVIEW.TREND_TITLE')} description={t(data?.granularity === 'week' ? 'BRAND_PORTAL.OVERVIEW.TREND_WEEK' : 'BRAND_PORTAL.OVERVIEW.TREND_DAY')} testId="brand-product-trend">
                                <div dir="ltr">
                                    <TrendChart data={data ? (trendPoints(data.trend, lang) as any) : []} valueLabel={t('BRAND_PORTAL.COMMON.THIS_PERIOD')} previousLabel={t('BRAND_PORTAL.COMMON.PREVIOUS_PERIOD')} height={220} partialLast partialLabel={t('VIZ_ANALYTICS.TREND.SO_FAR')} />
                                </div>
                            </SectionCard>
                            <SectionCard className="min-w-0 lg:col-span-2" title={t('BRAND_PORTAL.PRODUCT.FUNNEL_TITLE')} description={t('BRAND_PORTAL.PRODUCT.FUNNEL_TEXT')} testId="brand-product-funnel">
                                {data?.funnel?.length ? (
                                    <>
                                        <Funnel
                                            stages={data.funnel.filter((s) => s.shoppers != null).map((s) => ({ key: s.key, label: t(`BRAND_PORTAL.FUNNEL.${s.key.toUpperCase()}`), value: s.shoppers as number }))}
                                        />
                                        {data.funnel.some((s) => s.shoppers == null) && <p className="mt-3 text-xs text-muted-foreground">{t('BRAND_PORTAL.PRODUCT.FUNNEL_WITHHELD')}</p>}
                                    </>
                                ) : null}
                            </SectionCard>
                        </div>

                        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                            <SectionCard className="min-w-0" title={t('BRAND_PORTAL.DIMENSION.COUNTRY')} testId="brand-product-regions">
                                {breakdownList(data?.regions, 'country')}
                            </SectionCard>
                            <SectionCard className="min-w-0" title={t('BRAND_PORTAL.DIMENSION.CITY')}>
                                {breakdownList(data?.cities, 'city')}
                            </SectionCard>
                            <SectionCard className="min-w-0" title={t('BRAND_PORTAL.DIMENSION.ROOMTYPE')}>
                                {breakdownList(data?.roomTypes, 'roomType')}
                            </SectionCard>
                        </div>
                    </>
                )
            )}

            {data && (
                <SectionCard flush title={t('BRAND_PORTAL.PRODUCT.RETAILERS_TITLE', { count: data.product.retailerCount })} description={t('BRAND_PORTAL.PRODUCT.RETAILERS_TEXT')} testId="brand-product-retailers">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t('BRAND_PORTAL.PRODUCT.RETAILER')}</TableHead>
                                <TableHead className="text-end">{t('BRAND_PORTAL.KPI.VISUALIZATIONS')}</TableHead>
                                <TableHead className="text-end">{t('BRAND_PORTAL.KPI.SHOPPERS')}</TableHead>
                                <TableHead className="text-end">{t('BRAND_PORTAL.KPI.LEADS')}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {data.retailers.rows.map((r, i) => (
                                <TableRow key={i}>
                                    <TableCell className="max-w-[320px]">
                                        <ShopNameText label={r.label} />
                                        {!r.sharesDemand && <span className="ms-2 text-xs text-muted-foreground">{t('BRAND_PORTAL.PRODUCT.NOT_SHARING')}</span>}
                                    </TableCell>
                                    <TableCell className="text-end tabular-nums">{r.visualizations == null ? <Withheld /> : formatNumber(r.visualizations)}</TableCell>
                                    <TableCell className="text-end tabular-nums">{r.shoppers == null ? <Withheld /> : formatNumber(r.shoppers)}</TableCell>
                                    <TableCell className="text-end tabular-nums">{r.leads == null ? <Withheld /> : formatNumber(r.leads)}</TableCell>
                                </TableRow>
                            ))}
                            {data.retailers.otherRetailers && (
                                <TableRow className="text-muted-foreground">
                                    <TableCell>{t('BRAND_PORTAL.PRODUCT.OTHER_RETAILERS', { count: data.retailers.otherRetailers.retailers })}</TableCell>
                                    <TableCell className="text-end tabular-nums">{formatNumber(data.retailers.otherRetailers.visualizations)}</TableCell>
                                    <TableCell className="text-end tabular-nums">{formatNumber(data.retailers.otherRetailers.shoppers)}</TableCell>
                                    <TableCell className="text-end">
                                        <Withheld />
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </SectionCard>
            )}
            {!data?.suppressed && <PrivacyFootnote />}
        </BrandPage>
    );
}

export default BrandProductPage;
